import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Search,
  RefreshCw,
  AlertTriangle,
  Package,
  BarChart3,
  ArrowRight,
  Filter,
  Sparkles,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import { DataQualityWheel } from '@/components/sector/DataQualityWheel';

interface CategoryQuality {
  category: string;
  product_count: number;
  enriched_count: number;
  enrichment_pct: number;
  needs_review_count: number;
  needs_review_pct: number;
  avg_attributes: number;
  top_attributes: {
    name: string;
    count: number;
    coverage: number;
  }[];
}

interface Summary {
  total_products: number;
  total_enriched: number;
  total_needs_review: number;
  overall_enrichment_pct: number;
  overall_review_pct: number;
  category_count: number;
}

export default function DataQualityAudit() {
  const { selectedSite } = useSite();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [categories, setCategories] = useState<CategoryQuality[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'wheel' | 'table'>('wheel');
  const [sortBy, setSortBy] = useState<'products' | 'enrichment' | 'review'>('products');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!selectedSite) return;

    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(
        `${API_URL}/enrichment-analytics/site/${selectedSite.id || selectedSite._id}/category-readiness`
      );
      const data = await response.json();

      if (data.success) {
        setSummary(data.data.summary);
        setCategories(data.data.categories);
      } else {
        setError(data.error || 'Failed to fetch category readiness data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [selectedSite]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCategoryClick = (category: CategoryQuality) => {
    // Navigate to Enrichment Review page with category filter
    navigate(`/research/enrichment-review?category=${encodeURIComponent(category.category)}`);
  };

  const filteredCategories = categories.filter((cat) =>
    cat.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedCategories = [...filteredCategories].sort((a, b) => {
    switch (sortBy) {
      case 'products':
        return b.product_count - a.product_count;
      case 'enrichment':
        return b.enrichment_pct - a.enrichment_pct;
      case 'review':
        return b.needs_review_count - a.needs_review_count;
      default:
        return 0;
    }
  });

  const getEnrichmentColor = (pct: number) => {
    if (pct >= 80) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    if (pct >= 50) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    if (pct >= 20) return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
    return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
  };

  const getDisplayName = (category: string) => {
    const parts = category.split(' > ');
    return parts[parts.length - 1] || category;
  };

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Please select a site from the header to audit data quality.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Deprecation Banner */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 flex items-center justify-between">
        <p className="text-sm text-yellow-800 dark:text-yellow-200">
          This page is being consolidated into the unified <strong>Data Quality</strong> dashboard.
        </p>
        <a href="/data-quality" className="text-sm font-medium text-yellow-700 dark:text-yellow-300 hover:underline whitespace-nowrap ml-4">
          Go to Data Quality &rarr;
        </a>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-primary" />
            Data Quality Audit
          </h1>
          <p className="text-muted-foreground mt-1">
            Category-level view of AI enrichment coverage and review status
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === 'wheel' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('wheel')}
              className="rounded-none"
            >
              Wheel
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              className="rounded-none"
            >
              Table
            </Button>
          </div>
          <Button onClick={fetchData} variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Products
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.total_products.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card className="border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-950/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-green-700 dark:text-green-400">
                AI Enriched
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                {summary.total_enriched.toLocaleString()}
              </div>
              <div className="text-sm text-green-600 dark:text-green-500">
                {summary.overall_enrichment_pct}% coverage
              </div>
            </CardContent>
          </Card>
          <Card className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-amber-700 dark:text-amber-400">
                Needs Review
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-700 dark:text-amber-400">
                {summary.total_needs_review.toLocaleString()}
              </div>
              <div className="text-sm text-amber-600 dark:text-amber-500">
                {summary.overall_review_pct}% of products
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Categories
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.category_count}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Not Enriched
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-500">
                {(summary.total_products - summary.total_enriched).toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">
                {(100 - summary.overall_enrichment_pct).toFixed(1)}% remaining
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Wheel View */}
      {!loading && viewMode === 'wheel' && categories.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Category Enrichment Overview</CardTitle>
            <CardDescription>
              Click on a category segment to review its products
            </CardDescription>
          </CardHeader>
          <CardContent className="h-[700px]">
            <DataQualityWheel
              data={categories.slice(0, 30)} // Limit to 30 for readability
              onCategoryClick={handleCategoryClick}
              centerLabel="Data Quality"
              centerStats={summary ? {
                total_products: summary.total_products,
                total_enriched: summary.total_enriched,
                total_needs_review: summary.total_needs_review,
                overall_enrichment_pct: summary.overall_enrichment_pct,
              } : undefined}
            />
          </CardContent>
        </Card>
      )}

      {/* Table View */}
      {!loading && viewMode === 'table' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Category Details</CardTitle>
                <CardDescription>
                  {sortedCategories.length} categories • Click to expand
                </CardDescription>
              </div>
              <div className="flex gap-2 items-center">
                <div className="relative w-64">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search categories..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <select
                  className="border rounded-md px-3 py-2 text-sm bg-background"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'products' | 'enrichment' | 'review')}
                >
                  <option value="products">Sort by Products</option>
                  <option value="enrichment">Sort by Enrichment %</option>
                  <option value="review">Sort by Needs Review</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {sortedCategories.map((cat) => (
                <div
                  key={cat.category}
                  className="border rounded-lg overflow-hidden"
                >
                  {/* Category Row */}
                  <div
                    className="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer"
                    onClick={() => setExpandedCategory(
                      expandedCategory === cat.category ? null : cat.category
                    )}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        {expandedCategory === cat.category ? (
                          <ChevronUp className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium">{getDisplayName(cat.category)}</div>
                        <div className="text-xs text-muted-foreground">{cat.category}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-medium">{cat.product_count.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">products</div>
                      </div>
                      <Badge className={getEnrichmentColor(cat.enrichment_pct)}>
                        {cat.enrichment_pct}% enriched
                      </Badge>
                      {cat.needs_review_count > 0 && (
                        <Badge variant="outline" className="border-amber-400 text-amber-600">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          {cat.needs_review_count} review
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCategoryClick(cat);
                        }}
                      >
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedCategory === cat.category && (
                    <div className="border-t bg-muted/30 p-4">
                      <div className="grid grid-cols-2 gap-6">
                        {/* Stats */}
                        <div>
                          <h4 className="font-medium mb-3 flex items-center gap-2">
                            <BarChart3 className="h-4 w-4" />
                            Statistics
                          </h4>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Products:</span>
                              <span className="font-medium">{cat.product_count.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Enriched:</span>
                              <span className="font-medium">{cat.enriched_count.toLocaleString()} ({cat.enrichment_pct}%)</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Avg Attributes:</span>
                              <span className="font-medium">{cat.avg_attributes || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Needs Review:</span>
                              <span className={cat.needs_review_count > 0 ? "font-medium text-amber-600" : "font-medium"}>
                                {cat.needs_review_count} ({cat.needs_review_pct}%)
                              </span>
                            </div>
                            <div className="pt-2">
                              <div className="text-xs text-muted-foreground mb-1">Enrichment Progress</div>
                              <Progress value={cat.enrichment_pct} className="h-2" />
                            </div>
                          </div>
                        </div>

                        {/* Top Attributes */}
                        <div>
                          <h4 className="font-medium mb-3 flex items-center gap-2">
                            <Sparkles className="h-4 w-4" />
                            Top Attributes
                          </h4>
                          {cat.top_attributes.length > 0 ? (
                            <div className="space-y-2">
                              {cat.top_attributes.slice(0, 6).map((attr) => (
                                <div key={attr.name} className="flex justify-between items-center">
                                  <span className="text-sm">{attr.name}</span>
                                  <div className="flex items-center gap-2">
                                    <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-primary"
                                        style={{ width: `${attr.coverage}%` }}
                                      />
                                    </div>
                                    <span className="text-xs text-muted-foreground w-12 text-right">
                                      {attr.coverage}%
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-sm text-muted-foreground">
                              No attributes extracted yet
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 mt-4 pt-4 border-t">
                        <Button size="sm" onClick={() => handleCategoryClick(cat)}>
                          <Filter className="h-4 w-4 mr-2" />
                          Review Products
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            // Navigate to product catalog with category filter
                            navigate(`/about-business/offering?tab=catalog&category=${encodeURIComponent(cat.category)}`);
                          }}
                        >
                          <Package className="h-4 w-4 mr-2" />
                          View in Catalog
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Data */}
      {!loading && categories.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
            <p className="text-lg font-medium">No category data available</p>
            <p className="text-sm mt-1">Import products or run enrichment to see category statistics.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
